/**
 * src/constants/profileConstants.js
 *
 * Comprehensive profile system configuration.
 * Covers validation, display, privacy, UI, messages, and all feature-specific settings.
 
 * Organization:
 * - Validation rules and patterns
 * - Public/private field definitions
 * - Form field configurations
 * - Location and date formatting
 * - Display and pagination settings
 * - UI configuration (colors, sizes, spacing)
 * - Message definitions
 * - Error handling configuration
 * - Privacy settings
 * - Tabs and navigation
 * - Action menu items
 */

// ═══════════════════════════════════════════════════════════════════════════════
// VALIDATION RULES & PATTERNS
// ═══════════════════════════════════════════════════════════════════════════════

export const PROFILE_VALIDATION = {
  NAME: {
    MIN_LENGTH: 1,
    MAX_LENGTH: 100,
    PATTERN: /^[a-zA-Z\s'-]{1,100}$/,
    REQUIRED: true,
  },
  EMAIL: {
    PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    REQUIRED: false,
  },
  PHONE: {
    // Rwanda phone format: +250 7XX XXX XXX
    PATTERN: /^(\+250|0)?[7-9][0-9]{8}$/,
    REQUIRED: false,
    PLACEHOLDER_KEY: "profile.fields.phonePlaceholder",
  },
  BIO: {
    MIN_LENGTH: 0,
    MAX_LENGTH: 500,
    WARNING_THRESHOLD: 400,
    REQUIRED: false,
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// PRIVACY FIELD DEFINITIONS
// ═══════════════════════════════════════════════════════════════════════════════

export const PROFILE_PUBLIC_FIELDS = {
  EMAIL: "email",
  PHONE: "phone",
  LOCATION: "location",
  AVATAR: "avatar",
  BIO: "bio",
};

export const DEFAULT_PUBLIC_FIELDS = {
  [PROFILE_PUBLIC_FIELDS.EMAIL]: false,
  [PROFILE_PUBLIC_FIELDS.PHONE]: false,
  [PROFILE_PUBLIC_FIELDS.LOCATION]: true,
  [PROFILE_PUBLIC_FIELDS.AVATAR]: true,
  [PROFILE_PUBLIC_FIELDS.BIO]: true,
};

/**
 * Privacy field configuration with i18n keys and descriptions
 * Used in EditProfile.js to render privacy toggles
 */
export const PRIVACY_FIELDS_CONFIG = [
  {
    field: PROFILE_PUBLIC_FIELDS.EMAIL,
    labelKey: "profile.privacy.fields.email.label",
    descriptionKey: "profile.privacy.fields.email.description",
    icon: "Mail",
  },
  {
    field: PROFILE_PUBLIC_FIELDS.PHONE,
    labelKey: "profile.privacy.fields.phone.label",
    descriptionKey: "profile.privacy.fields.phone.description",
    icon: "Phone",
  },
  {
    field: PROFILE_PUBLIC_FIELDS.LOCATION,
    labelKey: "profile.privacy.fields.location.label",
    descriptionKey: "profile.privacy.fields.location.description",
    icon: "MapPin",
  },
  {
    field: PROFILE_PUBLIC_FIELDS.AVATAR,
    labelKey: "profile.privacy.fields.avatar.label",
    descriptionKey: "profile.privacy.fields.avatar.description",
    icon: "Image",
  },
  {
    field: PROFILE_PUBLIC_FIELDS.BIO,
    labelKey: "profile.privacy.fields.bio.label",
    descriptionKey: "profile.privacy.fields.bio.description",
    icon: "FileText",
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// FORM FIELD CONFIGURATIONS
// ═══════════════════════════════════════════════════════════════════════════════

export const FORM_FIELDS_CONFIG = {
  NAME: {
    name: "name",
    labelKey: "profile.fields.fullName",
    placeholderKey: "profile.fields.namePlaceholder",
    type: "text",
    required: true,
    validation: PROFILE_VALIDATION.NAME,
    errorKeyRequired: "profile.validation.nameRequired",
    errorKeyPattern: "profile.validation.nameInvalid",
  },
  EMAIL: {
    name: "email",
    labelKey: "profile.fields.email",
    placeholderKey: "profile.fields.emailPlaceholder",
    type: "email",
    required: false,
    validation: PROFILE_VALIDATION.EMAIL,
    errorKey: "profile.validation.invalidEmail",
  },
  PHONE: {
    name: "phone",
    labelKey: "profile.fields.phone",
    placeholderKey: "profile.fields.phonePlaceholder",
    type: "tel",
    required: false,
    validation: PROFILE_VALIDATION.PHONE,
    errorKey: "profile.validation.invalidPhone",
  },
  WHATSAPP: {
    name: "whatsapp",
    labelKey: "profile.fields.whatsapp",
    placeholderKey: "profile.fields.whatsappPlaceholder",
    type: "tel",
    required: false,
    validation: PROFILE_VALIDATION.PHONE,
    errorKey: "profile.validation.invalidPhone",
  },
  BIO: {
    name: "bio",
    labelKey: "profile.fields.bio",
    placeholderKey: "profile.fields.bioPlaceholder",
    type: "textarea",
    required: false,
    rows: 3,
    validation: PROFILE_VALIDATION.BIO,
    errorKey: "profile.validation.bioLength",
  },
  AVATAR: {
    name: "avatar",
    labelKey: "profile.fields.profilePhoto",
    type: "upload",
    required: false,
  },
  LOCATION: {
    name: "location",
    labelKey: "profile.sections.location",
    type: "location_picker",
    required: false,
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// LOCATION CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════

export const DEFAULT_LOCATION = {
  country: "Rwanda",
  province: "",
  district: "",
  sector: "",
  cell: "",
  village: "",
};

/**
 * Location field order for display formatting
 * Used when displaying location: village, sector, district
 */
export const LOCATION_DISPLAY_ORDER = ["village", "sector", "district", "cell"];

/**
 * Location hierarchy structure
 * Maps how location fields depend on each other
 */
export const LOCATION_HIERARCHY = {
  country: { label: "Country", level: 0 },
  province: { label: "Province", level: 1 },
  district: { label: "District", level: 2 },
  sector: { label: "Sector", level: 3 },
  cell: { label: "Cell", level: 4 },
  village: { label: "Village", level: 5 },
};

// ═══════════════════════════════════════════════════════════════════════════════
// DATE & TIME FORMATTING
// ═══════════════════════════════════════════════════════════════════════════════

export const DATE_FORMAT_CONFIG = {
  JOINED_DATE: {
    locale: "en-US",
    options: { month: "long", year: "numeric" },
  },
  FULL_DATE: {
    locale: "en-US",
    options: { year: "numeric", month: "long", day: "numeric" },
  },
  SHORT_DATE: {
    locale: "en-US",
    options: { month: "short", day: "numeric", year: "2-digit" },
  },
  TIME_FORMAT: {
    locale: "en-US",
    options: { hour: "2-digit", minute: "2-digit" },
  },
};

/**
 * Default fallback date for when createdAt is missing
 * Format: timestamp in milliseconds
 */
export const DEFAULT_JOIN_DATE = new Date("2025-01-01").getTime();

// ═══════════════════════════════════════════════════════════════════════════════
// BIO CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════

export const BIO_CONFIG = {
  MAX_LENGTH: 500,
  WARNING_THRESHOLD: 400, // Show warning at this character count
  CHAR_COUNTER_VISIBLE: true,
  PREVIEW_ENABLED: true,
};

// ═══════════════════════════════════════════════════════════════════════════════
// AVATAR & IMAGE CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════

export const AVATAR_CONSTRAINTS = {
  MAX_SIZE_MB: 5,
  ACCEPTED_FORMATS: ["image/jpeg", "image/png", "image/webp"],
  ACCEPTED_EXTENSIONS: [".jpg", ".jpeg", ".png", ".webp"],
  DIMENSIONS: {
    RECOMMENDED_WIDTH: 400,
    RECOMMENDED_HEIGHT: 400,
    MIN_WIDTH: 100,
    MIN_HEIGHT: 100,
  },
  COMPRESSION: {
    QUALITY: 0.8,
    MAX_WIDTH: 800,
    MAX_HEIGHT: 800,
  },
};

export const DEFAULT_AVATAR = "/default-avatar.png";

// ═══════════════════════════════════════════════════════════════════════════════
// DISPLAY & GRID CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════

export const PROFILE_DISPLAY = {
  // Grid columns for responsive design
  GRID_COLUMNS: {
    MOBILE: 1, // sm: 1 column
    TABLET: 2, // md: 2 columns
    DESKTOP: 3, // lg: 3 columns
    XLARGE: 4, // xl: 4 columns
  },
  // Items to show per page before "View All" button
  ITEMS_PER_PAGE: 6,
  // Pagination settings
  PAGINATION: {
    ITEMS_PER_PAGE: 12,
    MAX_PAGES: 100,
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// PROFILE TABS
// ═══════════════════════════════════════════════════════════════════════════════

export const PROFILE_TABS = {
  PRODUCTS: "products",
  WISHLIST: "wishlist",
};

export const PROFILE_TABS_CONFIG = [
  {
    id: PROFILE_TABS.PRODUCTS,
    labelKey: "profile.tabs.products",
     icon: "ShoppingBag",
   },
   {
     id: PROFILE_TABS.WISHLIST,
     labelKey: "profile.tabs.wishlist",
     icon: "Heart",
   },
];

// ═══════════════════════════════════════════════════════════════════════════════
// LOADING STATES
// ═══════════════════════════════════════════════════════════════════════════════

export const PROFILE_LOADING_STATES = {
  PRODUCTS: "loadingProducts",
  WISHLIST: "loadingWishlist",
  PROFILE: "loadingProfile",
  REVIEWS: "loadingReviews",
};

// ═══════════════════════════════════════════════════════════════════════════════
// TIMEOUT & PERFORMANCE CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════

export const PROFILE_TIMEOUTS = {
  LISTENER_TIMEOUT: 10000, // 10s for real-time listeners
  FETCH_TIMEOUT: 8000, // 8s for API calls
  DEBOUNCE_FORM: 300, // 300ms debounce on form input
  SUCCESS_MESSAGE_DURATION: 1500, // Show success message for 1.5s
  ERROR_MESSAGE_DURATION: 5000, // Show error message for 5s
};

// ═══════════════════════════════════════════════════════════════════════════════
// ACTION MENU CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════

export const ACTION_MENU_ITEMS = {
  EDIT_PROFILE: {
    id: "edit",
    labelKey: "profile.view.actions.edit",
    icon: "Edit3",
    color: "text-gray-700 dark:text-gray-300",
    visibleFor: "owner",
  },
  SHARE_PROFILE: {
    id: "share",
    labelKey: "profile.view.actions.share",
    icon: "Share2",
    color: "text-gray-700 dark:text-gray-300",
    visibleFor: "all",
  },
  CONTACT_INFO: {
    id: "contact",
    labelKey: "profile.view.actions.contact",
    icon: "User",
    color: "text-gray-700 dark:text-gray-300",
    visibleFor: "all",
  },
  REPORT_USER: {
    id: "report",
    labelKey: "profile.view.actions.report",
    icon: "Flag",
    color: "text-red-600 dark:text-red-400",
    visibleFor: "visitor",
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// PRIVACY & PROFILE VISIBILITY CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════

export const PROFILE_VISIBILITY = {
  PUBLIC: {
    id: "public",
    labelKey: "profile.view.privacy.publicProfile",
    descriptionKey: "profile.view.privacy.publicDescription",
    icon: "Eye",
  },
  PRIVATE: {
    id: "private",
    labelKey: "profile.view.privacy.privateProfile",
    descriptionKey: "profile.view.privacy.privateDescription",
    icon: "EyeOff",
  },
};

export const VISIBILITY_TOGGLE_STATES = {
  VISIBLE: "Visible",
  HIDDEN: "Hidden",
};

// ═══════════════════════════════════════════════════════════════════════════════
// SHARE CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════

export const SHARE_CONFIG = {
  FALLBACK_NAME: "User",
  APP_NAME: "Orrith - Cycle of Certainty",
  SHARE_SUCCESS_TIMEOUT: 2000, // Time to show "Copied!" message
};

// ═══════════════════════════════════════════════════════════════════════════════
// REPORT CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════

export const REPORT_CONFIG = {
  MIN_LENGTH: 10,
  MAX_LENGTH: 500,
  REPORT_TYPES: [
    {
      id: "inappropriate",
      labelKey: "profile.view.report.types.inappropriate",
    },
    {
      id: "spam",
      labelKey: "profile.view.report.types.spam",
    },
    {
      id: "fraud",
      labelKey: "profile.view.report.types.fraud",
    },
    {
      id: "harassment",
      labelKey: "profile.view.report.types.harassment",
    },
    {
      id: "other",
      labelKey: "profile.view.report.types.other",
    },
  ],
};

// ═══════════════════════════════════════════════════════════════════════════════
// DELETE CONFIRMATION CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════

export const DELETE_CONFIRMATION = {
  TITLE_KEY: "profile.view.actions.deleteConfirm",
  MESSAGE_KEY: "profile.view.actions.deleteConfirmDesc",
  CONFIRM_KEY: "profile.view.actions.delete",
  CANCEL_KEY: "common.cancel",
};

// ═══════════════════════════════════════════════════════════════════════════════
// PROFILE SECTIONS CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════

export const PROFILE_SECTIONS = {
  BASIC_INFO: {
    id: "basicInfo",
    titleKey: "profile.sections.basicInfo",
    fields: ["name", "bio", "avatar"],
  },
  CONTACT_INFO: {
    id: "contactInfo",
    titleKey: "profile.sections.contactInfo",
    fields: ["email", "phone", "whatsapp", "location"],
  },
  PRIVACY_SETTINGS: {
    id: "privacySettings",
    titleKey: "profile.sections.privacySettings",
    fields: ["profilePublic", "publicFields"],
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// ERROR RECOVERY & HANDLING
// ═══════════════════════════════════════════════════════════════════════════════

export const ERROR_RECOVERY = {
  AUTO_DISMISS_MS: 5000,
  SHOW_SUPPORT_LINK: true,
  // Current domain: orrith.me
  // Future domains to support: orrith.rw, orrith.com
  SUPPORT_EMAIL: "support@orrith.me",
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY_MS: 1000,
};

// ═══════════════════════════════════════════════════════════════════════════════
// ICON CONFIGURATION FOR EMPTY STATES
// ═══════════════════════════════════════════════════════════════════════════════

export const EMPTY_STATE_ICONS = {
  PRODUCTS: "ShoppingBag",
  WISHLIST: "Heart",
  PROFILE: "User",
};

// ═══════════════════════════════════════════════════════════════════════════════
// CONTACT INFORMATION DISPLAY
// ═══════════════════════════════════════════════════════════════════════════════

export const CONTACT_INFO_CONFIG = {
  EMAIL: {
    field: PROFILE_PUBLIC_FIELDS.EMAIL,
    icon: "Mail",
    labelKey: "profile.contact.email",
  },
  PHONE: {
    field: PROFILE_PUBLIC_FIELDS.PHONE,
    icon: "Phone",
    labelKey: "profile.contact.phone",
  },
  LOCATION: {
    field: PROFILE_PUBLIC_FIELDS.LOCATION,
    icon: "MapPin",
    labelKey: "profile.contact.location",
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// UI/UX CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════

export const UI_CONFIG = {
  COLORS: {
    PRIMARY: "green-600",
    PRIMARY_HOVER: "green-700",
    SUCCESS: "green-100",
    SUCCESS_TEXT: "green-800",
    DANGER: "red-500",
    WARNING: "yellow-600",
    NEUTRAL: "gray-700",
  },
  DARK_COLORS: {
    PRIMARY: "green-600",
    SUCCESS_BG: "green-900",
    SUCCESS_TEXT: "green-200",
    DANGER_TEXT: "red-400",
    WARNING_BG: "yellow-900/20",
    WARNING_TEXT: "yellow-300",
  },
  SPACING: {
    CARD_PADDING: "p-4 sm:p-6",
    SECTION_GAP: "gap-4 sm:gap-6",
    INPUT_PADDING: "p-3",
  },
  BREAKPOINTS: {
    MOBILE: "sm:",
    TABLET: "md:",
    DESKTOP: "lg:",
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// BUTTON CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════

export const BUTTON_CONFIG = {
  PRIMARY: {
    classes: "bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50",
    loadingStateKey: "Saving...",
  },
  SECONDARY: {
    classes: "border border-gray-300 dark:border-neutral-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-700",
  },
  DANGER: {
    classes: "bg-red-500 text-white rounded-lg hover:bg-red-600",
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// FOLLOW & INTERACTION CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════

export const FOLLOW_CONFIG = {
  DEBOUNCE_MS: 500,
  SHOW_FOLLOWER_MODAL: true,
  MAX_VISIBLE_FOLLOWERS: 50,
};

// ═══════════════════════════════════════════════════════════════════════════════
// ACCESSIBILITY & ARIA LABELS
// ═══════════════════════════════════════════════════════════════════════════════

export const ARIA_LABELS = {
  EDIT_PROFILE: "Edit your profile",
  UPLOAD_AVATAR: "Upload profile photo",
  TOGGLE_PRIVACY: "Toggle privacy setting",
  OPEN_MENU: "Open profile menu",
  CLOSE_MENU: "Close profile menu",
};

// ═══════════════════════════════════════════════════════════════════════════════
// EXPORT ALL AS OBJECT FOR CONVENIENCE
// ═══════════════════════════════════════════════════════════════════════════════

export const PROFILE_CONSTANTS = {
  VALIDATION: PROFILE_VALIDATION,
  PUBLIC_FIELDS: PROFILE_PUBLIC_FIELDS,
  DEFAULT_PUBLIC_FIELDS,
  PRIVACY_FIELDS_CONFIG,
  FORM_FIELDS_CONFIG,
  DEFAULT_LOCATION,
  LOCATION_DISPLAY_ORDER,
  LOCATION_HIERARCHY,
  DATE_FORMAT_CONFIG,
  BIO_CONFIG,
  AVATAR_CONSTRAINTS,
  DISPLAY: PROFILE_DISPLAY,
  TABS: PROFILE_TABS,
  TABS_CONFIG: PROFILE_TABS_CONFIG,
  LOADING_STATES: PROFILE_LOADING_STATES,
  TIMEOUTS: PROFILE_TIMEOUTS,
  ACTION_MENU_ITEMS,
  VISIBILITY: PROFILE_VISIBILITY,
  SHARE_CONFIG,
  REPORT_CONFIG,
  DELETE_CONFIRMATION,
  SECTIONS: PROFILE_SECTIONS,
  ERROR_RECOVERY,
  ICONS: EMPTY_STATE_ICONS,
  CONTACT_INFO_CONFIG,
  UI_CONFIG,
  BUTTON_CONFIG,
  FOLLOW_CONFIG,
  ARIA_LABELS,
};



